import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { clearIncidentsByAssetId, fetchIncidentsByAssetId, setPageNumber, setPageSize } from '@application/redux/asset-specific-incidents/asset-incidents-slice';
import { SDataGrid } from '@ui/components/common/grid/SDataGrid';
import { Box, Button } from '@mui/material';
import { GlassesRegular } from '@fluentui/react-icons';
import { ScoreChip } from '@ui/components/common/chips';
import { fetchPreviewIncident } from '@application/redux/Incidents/incident-slice';
import IncidentPreviewDrawer from '@ui/pages/Incidents/components/IncidentPreviewDrawer';
import { convertUtcToLocal } from '@application/utilities/date-time-formater';
const Incidents = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { incidents, pageNumber, pageSize, loading } = useSelector((state) => state.IncidentByAssetId);
    const [systemDialog, setSystemDialog] = useState({
        show: false,
        variant: ''
    });
    const pathname = window.location.pathname;
    const segments = pathname.split('/');
    const id = segments[segments.length - 1];
    const [showIncidentPreviewDrawer, setShowIncidentPreviewDrawer] = useState(false);
    const isMountedRef = useRef(false);
    const prevValuesRef = useRef(null);
    const handleOpenIncidentPreviewDrawer = async (rowId) => {
        await dispatch(fetchPreviewIncident(rowId)).unwrap();
        setShowIncidentPreviewDrawer(true);
    };
    const handleCloseIncidentPreviewDrawer = () => {
        setShowIncidentPreviewDrawer(false);
    };
    useEffect(() => {
        if (!id)
            return;
        const currentValues = { id, pageNumber, pageSize };
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            prevValuesRef.current = currentValues;
            dispatch(fetchIncidentsByAssetId({ assetId: id, isManuallyEntered: false, pageNumber, pageSize }));
            return;
        }
        if (prevValuesRef.current) {
            const prev = prevValuesRef.current;
            const shouldFetch = prev.id !== id ||
                prev.pageNumber !== pageNumber ||
                prev.pageSize !== pageSize;
            if (shouldFetch) {
                prevValuesRef.current = currentValues;
                dispatch(fetchIncidentsByAssetId({ assetId: id, isManuallyEntered: false, pageNumber, pageSize }));
            }
        }
        return () => {
            dispatch(clearIncidentsByAssetId());
            isMountedRef.current = false;
            prevValuesRef.current = null;
        };
    }, [dispatch, id, pageNumber, pageSize]);
    const rows = incidents?.data.data.map((s) => ({
        id: s.id || '',
        systemName: s.systemName,
        licensePlate: s.licensePlate || '',
        incidentDate: s.incidentDate,
        score: s.score
    })) || [];
    const columns = [
        {
            field: 'systemName',
            headerName: t('SystemName'),
            minWidth: 150,
            flex: 1,
            editable: false,
            sortable: false,
            type: 'string'
        },
        {
            field: 'licensePlate',
            headerName: t('License#'),
            minWidth: 150,
            flex: 1,
            editable: false,
            type: 'string'
        },
        {
            field: 'incidentDate',
            headerName: t('Date/Time'),
            minWidth: 400,
            flex: 1.4,
            editable: false,
            type: 'dateTime',
            valueFormatter: (params) => convertUtcToLocal(params)
        },
        {
            field: 'score',
            headerName: t('Score'),
            type: 'number',
            align: 'left',
            headerAlign: 'left',
            minWidth: 140,
            flex: 1,
            editable: false,
            renderCell: (params) => _jsx(ScoreChip, { type: 'warning', label: params.value })
        },
        {
            field: '',
            headerName: '',
            minWidth: 120,
            display: 'flex',
            align: 'center',
            editable: false,
            sortable: false,
            type: 'actions',
            renderCell: (params) => {
                return (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, pr: '6px' }, children: _jsx(Button, { variant: 'outlined', color: 'info', startIcon: _jsx(GlassesRegular, { fontSize: 20 }), onClick: () => handleOpenIncidentPreviewDrawer(params.row.id), children: t('Preview') }) }));
            }
        }
    ];
    return (_jsxs(_Fragment, { children: [_jsx(Box, { className: 'table-container', children: _jsx(SDataGrid, { rows: rows, columns: columns, serverSidePagination: true, totalRows: incidents?.data?.totalRecords, pageSizeOptions: [10, 20, 50, 100], loading: loading, initialState: {
                        pagination: {
                            paginationModel: {
                                pageSize: incidents?.data.pageSize || 10,
                                page: incidents?.data.pageNumber || 0
                            }
                        }
                    }, onPaginationModelChange: (model) => {
                        dispatch(setPageNumber(model.page + 1));
                        dispatch(setPageSize(model.pageSize));
                    }, filters: {}, setFilters: () => { } }) }), showIncidentPreviewDrawer && (_jsx(IncidentPreviewDrawer, { open: showIncidentPreviewDrawer, onClose: handleCloseIncidentPreviewDrawer }))] }));
};
export default Incidents;
